import { useState, useEffect, useRef } from 'react';
import useMarketData from './useMarketData';
import websocketService from '../../../services/websocket';

/**
 * A hook that wraps useMarketData with caching functionality
 * @param {boolean} isWebSocketReady - Whether the WebSocket is ready for API calls
 * @returns {Object} The same API as useMarketData but with caching
 */
export default function useCachedMarketData(isWebSocketReady) {
  // Use the original hook
  const marketData = useMarketData(isWebSocketReady);
  
  // Cache state
  const [cacheTimestamp, setCacheTimestamp] = useState(null);
  const cacheTimeoutRef = useRef(null);
  
  // Cache TTL in milliseconds (5 minutes)
  const CACHE_TTL = 5 * 60 * 1000;
  
  // Wrap the fetchData function to include caching
  const fetchDataWithCache = async () => {
    const now = Date.now();
    
    // If cache is valid, use cached instrument data from websocketService
    if (cacheTimestamp && (now - cacheTimestamp < CACHE_TTL)) {
      console.log('[useCachedMarketData] Using cached market data');
      
      // Check if we have instruments cached for the current category
      const cachedInstruments = [];
      let hasCachedData = false;
      
      // Build formatted instruments from cache
      marketData.pageInstruments.forEach(instrument => {
        if (instrument.originalId) {
          const cachedInstrument = websocketService.getCachedInstrument(instrument.originalId);
          if (cachedInstrument) {
            hasCachedData = true;
            
            // Format cached data to match pageInstruments format
            cachedInstruments.push({
              ...instrument,
              price: typeof cachedInstrument.price === 'number' ? cachedInstrument.price : instrument.price,
              bidPrice: typeof cachedInstrument.bid === 'number' ? cachedInstrument.bid : instrument.bidPrice,
              askPrice: typeof cachedInstrument.ask === 'number' ? cachedInstrument.ask : instrument.askPrice,
              change: typeof cachedInstrument['24hour-change'] === 'number' ? 
                cachedInstrument['24hour-change'] : instrument.change
            });
          } else {
            cachedInstruments.push(instrument);
          }
        } else {
          cachedInstruments.push(instrument);
        }
      });
      
      // Update instruments with cached data if we found any
      if (hasCachedData) {
        marketData.setPageInstruments(cachedInstruments);
        return cachedInstruments;
      }
    }
    
    // No valid cache or no instruments in cache, fetch fresh data
    const result = await marketData.fetchData();
    
    // Set cache timestamp
    setCacheTimestamp(Date.now());
    
    // Set timeout to clear cache after TTL
    if (cacheTimeoutRef.current) {
      clearTimeout(cacheTimeoutRef.current);
    }
    
    cacheTimeoutRef.current = setTimeout(() => {
      console.log('[useCachedMarketData] Cache expired');
      setCacheTimestamp(null);
    }, CACHE_TTL);
    
    return result;
  };
  
  // Clean up the cache timeout on unmount
  useEffect(() => {
    return () => {
      if (cacheTimeoutRef.current) {
        clearTimeout(cacheTimeoutRef.current);
      }
    };
  }, []);
  
  // Create a wrapped version of fetchPageInstruments that uses the cache
  const fetchPageInstrumentsWithCache = async (page, count, category) => {
    const now = Date.now();
    
    // If cache is valid and we're not changing category, check for cached data
    if (cacheTimestamp && (now - cacheTimestamp < CACHE_TTL) && 
        category === marketData.selectedCategory) {
      console.log('[useCachedMarketData] Checking cache for page instruments');
      
      // We'll implement page-specific caching in the future
      // For now, rely on the normal fetch with potential cache from websocketService
    }
    
    // Always fetch page instruments normally
    const result = await marketData.fetchPageInstruments(page, count, category);
    
    // Update timestamp if we're fetching new data
    setCacheTimestamp(Date.now());
    
    return result;
  };
  
  // Return the same API but with wrapped cache-aware functions
  return {
    ...marketData,
    fetchData: fetchDataWithCache,
    fetchPageInstruments: fetchPageInstrumentsWithCache,
    isCacheValid: cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_TTL),
    clearCache: () => {
      setCacheTimestamp(null);
      if (cacheTimeoutRef.current) {
        clearTimeout(cacheTimeoutRef.current);
        cacheTimeoutRef.current = null;
      }
    }
  };
} 